// Generic sim-side map driver.
//
// Walks a MapJSON, instantiates primitive Rapier bodies/colliders for each
// physical object, then instantiates each behavior module and wires it
// through MapContext. Public surface matches the legacy Arena class so
// World.ts doesn't branch on "bundle or not".
//
// A bundle is loaded via `MapArena.create(world, bundle)` which is async —
// behavior modules are dynamic imports. The old `new Arena(world, mapId)`
// shape is synchronous, so main.ts awaits the bundle's behaviors during
// the same preload step that already handles spaceship-in-rings GLBs.

import RAPIER from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { packGroups } from './Ragdoll';
import type { Character } from './Character';
import type { MapBundle } from '../maps/registry';
import { getCachedBehaviorFactories } from '../maps/registry';
import type { Behavior, MapContext } from '../maps/Behavior';
import type { ImpactEvent } from './SimState';
import type {
  BodyKind, ColliderSpec, MapJSON, ObjectNode, Vec3
} from '../maps/MapJSON';

const EMPTY_IMPACTS: readonly ImpactEvent[] = Object.freeze([]);

const ARENA_GROUP_BIT = 1 << 0;
const ALL_FILTER = 0xffff;

export class MapArena {
  readonly mapId: string;
  readonly json: MapJSON;
  readonly killY: number;
  readonly radius: number;

  /** Primary floor/ground collider. For maps without a distinguished floor
   *  (spaceship-in-rings uses the ship trimesh as its main collider) this
   *  refers to the first collider registered. */
  readonly collider: RAPIER.Collider;

  /** Extra static/kinematic colliders produced by primitives. */
  readonly extraColliders: RAPIER.Collider[] = [];

  /** Lookup tables populated from JSON object ids + behavior registrations.
   *  Exposed as readonly so the render-side driver can resolve ids without
   *  duplicating the maps. */
  readonly bodyById = new Map<string, RAPIER.RigidBody>();
  readonly colliderById = new Map<string, RAPIER.Collider>();
  private behaviorById = new Map<string, Behavior>();

  private behaviors: Behavior[] = [];
  /** Readonly to MapArenaView for character lookup. */
  characterList: Character[] = [];
  /** Reference to the World's per-tick impacts buffer. Wired by World
   *  via `setImpactsRef()` after construction so behaviors can read the
   *  combat events without a direct World import. */
  private impactsRef: readonly ImpactEvent[] = EMPTY_IMPACTS;
  readonly rapierWorld: RAPIER.World;
  timeSec = 0;
  private readonly _windOut = { x: 0, z: 0 };
  /** True when this arena is the authoritative simulation (single-player or
   *  server). False in the multiplayer client's local world. Threaded into
   *  every MapContext the arena hands out so behaviors can branch on it. */
  readonly authoritative: boolean;
  private readonly behaviorParamOverrides: Record<string, Record<string, unknown>>;

  /** Synchronous constructor. The caller MUST have awaited
   *  `preloadBundle(bundle.id)` first — behavior factories are read from the
   *  module cache. Matches the established preloadSpaceshipAssets /
   *  getSpaceshipAssets pattern elsewhere in the codebase. */
  constructor(
    world: RAPIER.World,
    bundle: MapBundle,
    opts?: {
      authoritative?: boolean;
      behaviorParamOverrides?: Record<string, Record<string, unknown>>;
    }
  ) {
    this.rapierWorld = world;
    this.json = bundle.json;
    this.mapId = bundle.json.id;
    this.authoritative = opts?.authoritative ?? true;
    this.behaviorParamOverrides = opts?.behaviorParamOverrides ?? {};

    const props = bundle.json.properties ?? {};
    this.killY = props.killY ?? -8.0;

    // Build primitives and collect colliders. A single "primary" collider
    // is nominated so the old `arena.collider` surface still has something
    // to return.
    let primary: RAPIER.Collider | null = null;
    for (const node of bundle.json.objects ?? []) {
      const { mainCollider } = this.buildObject(node, null);
      if (mainCollider && !primary) primary = mainCollider;
    }
    if (!primary) {
      const bd = RAPIER.RigidBodyDesc.fixed().setTranslation(0, -1e6, 0);
      const body = world.createRigidBody(bd);
      const cd = RAPIER.ColliderDesc.ball(0.01)
        .setCollisionGroups(packGroups(ARENA_GROUP_BIT, ALL_FILTER));
      primary = world.createCollider(cd, body);
    }
    this.collider = primary;

    this.radius = props.radius ?? inferRadius(bundle.json) ?? 7.0;

    // Instantiate behaviors in JSON order so earlier behaviors can be looked
    // up by later ones in their init() bodies.
    const factories = getCachedBehaviorFactories(bundle.id);
    if (!factories && (bundle.json.behaviors?.length ?? 0) > 0) {
      throw new Error(
        `[MapArena] ${bundle.id}: behaviors not preloaded. ` +
        `Call \`await preloadBundle('${bundle.id}')\` before constructing.`
      );
    }
    const ctx = this.makeContext();
    for (const def of bundle.json.behaviors ?? []) {
      const factory = factories?.get(def.module);
      if (!factory) continue;
      const behavior = factory();
      this.behaviors.push(behavior);
      this.behaviorById.set(def.id, behavior);
      const override = this.behaviorParamOverrides[def.id];
      const mergedParams = override ? { ...(def.params ?? {}), ...override } : (def.params ?? {});
      behavior.init?.(ctx, mergedParams);
    }
  }

  private makeContext(): MapContext {
    const self = this;
    return {
      mapId: this.mapId,
      layer: 'sim',
      rapier: this.rapierWorld,
      scene: null,          // sim-side context; render ctx lives in MapArenaView
      mapGroup: null,
      time: () => self.timeSec,
      authoritative: this.authoritative,
      getObject3D: () => undefined,
      getBody: (id) => self.bodyById.get(id),
      getCollider: (id) => self.colliderById.get(id),
      getBehavior: <T extends Behavior>(id: string) =>
        self.behaviorById.get(id) as T | undefined,
      registerObject3D: () => { /* no-op on sim side */ },
      registerBody: (id, body) => { self.bodyById.set(id, body); },
      registerCollider: (id, col) => { self.colliderById.set(id, col); },
      characters: () => self.characterList,
      recentImpacts: () => self.impactsRef,
      camera: () => null,
      audioListener: () => null
    };
  }

  /** Wired by World after it allocates its impacts buffer. The reference
   *  is captured once; the array is reused (length cleared, then pushed
   *  to) each tick, so the captured pointer always sees the latest events. */
  setImpactsRef(ref: readonly ImpactEvent[]): void {
    this.impactsRef = ref;
  }

  /** Accessor used by MapArenaView to walk the behavior list for render-layer
   *  init + stepRender without duplicating the instances. */
  get behaviorList(): readonly Behavior[] { return this.behaviors; }
  get behaviorDefsById(): ReadonlyMap<string, Behavior> { return this.behaviorById; }

  /** Called by World after construction. Gives behaviors access to the
   *  character list for KO / wind / grab hooks. */
  registerVictims(chars: Character[]): void {
    this.characterList = chars;
  }

  // Primitive builder returning the primary collider (if any) for the
  // nomination of arena.collider. Recursive for group nodes. `parent` is
  // currently unused — primitives on the sim side are all world-space;
  // parent transforms are applied during construction so the engine doesn't
  // need to walk a group tree.
  private buildObject(
    node: ObjectNode,
    _parent: ObjectNode | null
  ): { mainCollider: RAPIER.Collider | null } {
    if (node.type === 'group') {
      let nominated: RAPIER.Collider | null = null;
      for (const child of node.children ?? []) {
        const { mainCollider } = this.buildObject(child, node);
        if (mainCollider && !nominated) nominated = mainCollider;
      }
      return { mainCollider: nominated };
    }
    // modelRef colliders are built by the consuming behavior (via
    // ctx.registerBody / ctx.registerCollider) because the GLB load is
    // async and the driver doesn't know how to slice a trimesh. We return
    // null here so the primary-collider nomination walks past it.
    if (node.type === 'modelRef') {
      return { mainCollider: null };
    }
    const spec = node.collider;
    if (!spec || spec.body === 'none') {
      return { mainCollider: null };
    }

    const p = readVec3(node.transform?.p, [0, 0, 0]);
    const r = readVec3(node.transform?.r, [0, 0, 0]);

    const bodyDesc = buildBodyDesc(spec.body ?? 'static', p, r, spec);
    const body = this.rapierWorld.createRigidBody(bodyDesc);
    const colDesc = buildColliderDesc(node, spec);
    const collider = this.rapierWorld.createCollider(colDesc, body);
    this.extraColliders.push(collider);
    if (node.id) {
      this.bodyById.set(node.id, body);
      this.colliderById.set(node.id, collider);
    }
    return { mainCollider: collider };
  }

  /** Per-tick — drives every behavior's sim step. Called from World.step
   *  after rapier.step so contact impulses get solved before behaviors
   *  apply their own forces. `simTimeSec` is the authoritative tick time
   *  (`world.tick / TICK_HZ`); see Behavior.stepSim for usage. */
  stepSim(dt: number, suppressDamage: boolean, simTimeSec: number): void {
    this.timeSec += dt;
    for (const b of this.behaviors) b.stepSim?.(dt, suppressDamage, simTimeSec);
  }

  /** Round-boundary hook. World.resetRound() calls this so behaviors with
   *  per-round timers (sinking-ship countdown, future moving-platform
   *  loops, etc.) re-zero alongside the character spawn reset. */
  resetRound(): void {
    for (const b of this.behaviors) b.resetRound?.();
  }

  /** Match-teardown: walk every behavior and let it release its sim-layer
   *  resources (timers, body refs, etc.). Render-layer disposal is the
   *  partner call on `MapArenaView.disposeBehaviors`; both share the same
   *  behavior instances, so each layer's dispose runs once. */
  disposeBehaviors(): void {
    for (const b of this.behaviors) b.dispose?.('sim');
  }

  /** Aggregate wind across all behaviors. */
  windAccel(): { x: number; z: number } {
    const out = this._windOut;
    out.x = 0; out.z = 0;
    for (const b of this.behaviors) {
      const w = b.windAccel?.();
      if (w) { out.x += w.x; out.z += w.z; }
    }
    return out;
  }

  isOutOfBounds(x: number, y: number, z: number): boolean {
    if (y < this.killY) return true;
    // Behaviors that want to replace the default radial check return
    // true / false; `undefined` means "no opinion — use default".
    for (const b of this.behaviors) {
      const r = b.isOutOfBoundsOverride?.(x, y, z);
      if (r !== undefined) return r;
    }
    // Default: circular-arena ring-out — matches legacy sandbox logic.
    const r = Math.hypot(x, z);
    return y < -1.0 && r > this.radius + 0.4;
  }
}

// ---------------------------------------------------------------------------
// Helpers

function readVec3(v: Vec3 | undefined, fallback: readonly [number, number, number]): Vec3 {
  return v ?? fallback;
}

function buildBodyDesc(
  kind: BodyKind,
  p: Vec3,
  r: Vec3,
  spec: ColliderSpec
): RAPIER.RigidBodyDesc {
  let bd: RAPIER.RigidBodyDesc;
  switch (kind) {
    case 'dynamic': bd = RAPIER.RigidBodyDesc.dynamic(); break;
    case 'kinematic': bd = RAPIER.RigidBodyDesc.kinematicPositionBased(); break;
    case 'static':
    default: bd = RAPIER.RigidBodyDesc.fixed();
  }
  bd.setTranslation(p[0], p[1], p[2]);
  if (r[0] || r[1] || r[2]) {
    const q = quatFromEuler(r[0], r[1], r[2]);
    bd.setRotation(q);
  }
  if (spec.gravityScale !== undefined) bd.setGravityScale(spec.gravityScale);
  return bd;
}

function buildColliderDesc(node: ObjectNode, spec: ColliderSpec): RAPIER.ColliderDesc {
  const kind = spec.kind ?? 'auto';
  let cd: RAPIER.ColliderDesc;
  if (kind === 'box' || (kind === 'auto' && node.type === 'box')) {
    const [x, y, z] = (node as { size: Vec3 }).size;
    cd = RAPIER.ColliderDesc.cuboid(x * 0.5, y * 0.5, z * 0.5);
  } else if (kind === 'sphere' || (kind === 'auto' && node.type === 'sphere')) {
    cd = RAPIER.ColliderDesc.ball((node as { radius: number }).radius);
  } else if (kind === 'cylinder' || (kind === 'auto' && node.type === 'cylinder')) {
    const c = node as { radiusTop: number; radiusBottom: number; height: number };
    cd = RAPIER.ColliderDesc.cylinder(c.height * 0.5, Math.max(c.radiusTop, c.radiusBottom));
  } else if (kind === 'capsule' || (kind === 'auto' && node.type === 'capsule')) {
    const c = node as { radius: number; halfHeight: number };
    cd = RAPIER.ColliderDesc.capsule(c.halfHeight, c.radius);
  } else if (kind === 'auto' && node.type === 'plane') {
    const [w, h] = node.size;
    cd = RAPIER.ColliderDesc.cuboid(w * 0.5, 0.01, h * 0.5);
  } else {
    // Fallback — the primitive doesn't have a well-formed physics shape.
    cd = RAPIER.ColliderDesc.ball(0.1);
  }
  if (spec.friction !== undefined) cd.setFriction(spec.friction);
  if (spec.restitution !== undefined) cd.setRestitution(spec.restitution);
  if (spec.density !== undefined) cd.setDensity(spec.density);
  cd.setCollisionGroups(packGroups(spec.membership ?? ARENA_GROUP_BIT, spec.filter ?? ALL_FILTER));
  return cd;
}

function quatFromEuler(x: number, y: number, z: number): { w: number; x: number; y: number; z: number } {
  // XYZ order via THREE — keep behavior consistent with what the editor will
  // render in its gizmo. Using three here is a light import vs. writing out
  // the trig ourselves; the sim side otherwise has no THREE dependency.
  const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(x, y, z, 'XYZ'));
  return { w: q.w, x: q.x, y: q.y, z: q.z };
}

function inferRadius(json: MapJSON): number | undefined {
  // Find the first top-level cylinder centred at (or near) the origin and
  // use its radius. Good enough for sandbox and any cylinder-platform map.
  for (const node of json.objects ?? []) {
    if (node.type !== 'cylinder') continue;
    const p = node.transform?.p ?? [0, 0, 0];
    if (Math.hypot(p[0], p[2]) > 0.5) continue;
    return Math.max(node.radiusTop, node.radiusBottom);
  }
  return undefined;
}
